import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'antd';
import {
  useBrandListQuery,
  useClientListQuery,
  useDealListQuery,
  useItemListQuery,
  useLeadListQuery,
  usePurchaseRequestListQuery,
  useSupplierQuery,
  useTasksListQuery,
} from 'api';
import { entityTypes } from './constants';
import { DealStatusBadge, LeadStatusBadge, PurchaseRequestStatusBadge, StatusBadge } from '../StatusBadge';
import { PriorityBadge } from '../priorityBadge';
import { useAccessControl } from '../accessControl/useAccessControl';

export const useGlobalSearch = () => {
  const { hasAccess } = useAccessControl();
  const filteredEntityTypesByPermissions = useMemo(() => {
    return entityTypes.filter((i) => hasAccess(i.permission));
  }, [hasAccess]);
  const [selectedEntityType, setSelectedEntityType] = useState<string>(
    filteredEntityTypesByPermissions?.[0]?.value || ''
  );
  const [searchText, setSearchText] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const selectedEntity = entityTypes.find((entity) => entity.value === selectedEntityType);

  // Query parameters
  const queryParams = useMemo(
    () => ({
      search: searchText,
      size: 10,
      page: 1,
    }),
    [searchText]
  );

  // Queries for each entity type
  const { data: clientData, isLoading: isClientLoading } = useClientListQuery(
    selectedEntityType === 'client' && searchText ? queryParams : { size: 0, page: 1 },
    { skip: selectedEntityType !== 'client' || !searchText }
  );

  const { data: brandData, isLoading: isBrandLoading } = useBrandListQuery(
    selectedEntityType === 'brand' && searchText ? queryParams : { size: 0, page: 1 },
    { skip: selectedEntityType !== 'brand' || !searchText }
  );

  const { data: supplierData, isLoading: isSupplierLoading } = useSupplierQuery(
    selectedEntityType === 'supplier' && searchText ? queryParams : { size: 0, page: 1 },
    { skip: selectedEntityType !== 'supplier' || !searchText }
  );

  const { data: itemData, isLoading: isItemLoading } = useItemListQuery(
    selectedEntityType === 'item' && searchText ? queryParams : { size: 0, page: 1 },
    { skip: selectedEntityType !== 'item' || !searchText }
  );

  const { data: leadData, isLoading: isLeadLoading } = useLeadListQuery(
    selectedEntityType === 'lead' && searchText ? queryParams : { size: 0, page: 1 },
    { skip: selectedEntityType !== 'lead' || !searchText }
  );

  const { data: dealData, isLoading: isDealLoading } = useDealListQuery(
    selectedEntityType === 'deal' && searchText ? queryParams : { size: 0, page: 1 },
    { skip: selectedEntityType !== 'deal' || !searchText }
  );

  const { data: purchaseRequestData, isLoading: isPurchaseRequestLoading } = usePurchaseRequestListQuery(
    selectedEntityType === 'purchaseRequest' && searchText ? queryParams : { size: 0, page: 1 },
    { skip: selectedEntityType !== 'purchaseRequest' || !searchText }
  );

  const { data: taskData, isLoading: isTaskLoading } = useTasksListQuery(
    selectedEntityType === 'task' && searchText ? { ...queryParams, isMainTable: true } : { size: 0, page: 1 },
    { skip: selectedEntityType !== 'task' || !searchText }
  );

  // Get the correct data and loading state based on selected entity
  const getEntityData = () => {
    switch (selectedEntityType) {
      case 'client':
        return {
          data: clientData?.data?.content?.[0]?.clients || [],
          isLoading: isClientLoading,
          getTitle: (item: any) => item.name,
          getDescription: (item: any) => `ID: ${item.uuid || ''}`,
          getAvatar: (item: any) => item.country?.fileLocation || null,
        };
      case 'brand':
        return {
          data: brandData?.data?.content?.[0]?.brands || [],
          isLoading: isBrandLoading,
          getTitle: (item: any) => item.name,
          getDescription: (item: any) => `ID: ${item.uuid || ''}`,
          getAvatar: (item: any) => item?.files?.LOGO || null,
        };
      case 'supplier':
        return {
          data: supplierData?.content || [],
          isLoading: isSupplierLoading,
          getTitle: (item: any) => item.name,
          getDescription: (item: any) => `ID: ${item.uuid || ''}`,
          getAvatar: (item: any) => item.country?.fileLocation || null,
        };
      case 'item':
        return {
          data: itemData?.content?.[0]?.items || [],
          isLoading: isItemLoading,
          getTitle: (item: any) => `(SKU: ${item.itemSKU}) ${item.description || ''}`,
          getDescription: (item: any) => `ID: ${item.uuid || ''}`,
          getAvatar: () => null,
        };
      case 'lead':
        return {
          data: leadData?.data?.content || [],
          isLoading: isLeadLoading,
          getTitle: (item: any) => (item.client?.name ? `Client: ${item.client?.name}` : ''),
          getDescription: (item: any) => (
            <Row style={{ width: 355 }} justify={'space-between'} wrap={false}>
              <Col style={{ padding: 0 }}>ID: {item.uuid || ''}</Col>
              <Col style={{ padding: 0, textAlign: 'right' }}>
                <LeadStatusBadge type={item.status} />
              </Col>
            </Row>
          ),
          getAvatar: () => null,
        };
      case 'deal':
        return {
          data: dealData?.data?.content || [],
          isLoading: isDealLoading,
          getTitle: (item: any) => (item.clientName ? `Client: ${item.clientName}` : ''),
          getDescription: (item: any) => (
            <Row style={{ width: 355 }} justify={'space-between'} wrap={false}>
              <Col style={{ padding: 0 }}>ID: {item.uuid || ''}</Col>
              <Col style={{ padding: 0, textAlign: 'right' }}>
                <DealStatusBadge type={item.status} />
              </Col>
            </Row>
          ),
          getAvatar: () => null,
        };
      case 'purchaseRequest':
        return {
          data: purchaseRequestData?.content?.[0]?.purchaseRequestResponses || [],
          isLoading: isPurchaseRequestLoading,
          getTitle: (item: any) => (item.client?.name ? `Client: ${item.client?.name}` : ''),
          getDescription: (item: any) => (
            <Row style={{ width: 355 }} justify={'space-between'} wrap={false}>
              <Col style={{ padding: 0 }}>ID: {item.uuid || ''}</Col>
              <Col style={{ padding: 0, textAlign: 'right' }}>
                <PurchaseRequestStatusBadge type={item.status} />
              </Col>
            </Row>
          ),
          getAvatar: () => null,
        };
      case 'task':
        return {
          data: taskData?.content || [],
          isLoading: isTaskLoading,
          getTitle: (item: any) => item.summary || `Task #${item.uuid || ''}`,
          getDescription: (item: any) => (
            <Row style={{ width: 355 }} justify={'space-between'} wrap={false}>
              <Col style={{ padding: 0 }}>
                <PriorityBadge type={item.priorityLevel} />
              </Col>
              <Col style={{ padding: 0, textAlign: 'right' }}>
                <StatusBadge type={item.status} />
              </Col>
            </Row>
          ),
          getAvatar: () => null,
        };
      default:
        return {
          data: [],
          isLoading: false,
          getTitle: () => '',
          getDescription: () => '',
          getAvatar: () => null,
        };
    }
  };

  const { data, isLoading, getTitle, getDescription, getAvatar } = getEntityData();

  useEffect(() => {
    setIsSearching(isLoading);
  }, [isLoading]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleEntityChange = (value: string) => {
    setSelectedEntityType(value);
    setSearchText('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    setShowResults(!!value);
  };

  const handleItemClick = (item: any) => {
    const entity = entityTypes.find((e) => e.value === selectedEntityType);
    if (entity) {
      let path = `${entity.path}/${item.id || item.value}`;
      if (entity.tab) {
        path = `${path}/${entity.tab}`;
      }
      navigate(path);
      setShowResults(false);
      setSearchText('');
    }
  };

  return {
    selectedEntityType,
    searchText,
    showResults,
    isSearching,
    data,
    selectedEntity,
    handleEntityChange,
    handleSearchChange,
    handleItemClick,
    getTitle,
    getDescription,
    setShowResults,
    getAvatar,
    searchRef,
    filteredEntityTypesByPermissions,
  };
};
